import React, { useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { IconButton } from "@/components/ui/icon-button"
import {
  Maximize2,
  ChevronDown,
  SlidersHorizontal,
  PanelRight,
  FileText,
  MessageSquare,
  Loader2,
  FilePlus,
  FolderPlus,
  Folder,
  MoreVertical,
  RefreshCw,
  Pencil,
  Move,
  Trash2,
} from "lucide-react"
import { NavigationButtons } from "@/components/ui/navigation-buttons"
import { AddressBar, type BreadcrumbSegment } from "@/components/ui/address-bar"
import { ListViewModeToggle } from "@/components/integrations/_shared/list-view-mode-toggle"
import { PreviewPanelToggle } from "@/components/integrations/_shared/preview-panel-toggle"
import { testIds } from "@/lib/test-ids"
import type {
  FilterType,
  ListSortColumn,
  SortDir,
  ViewMode,
  TodoFolderStripData,
} from "./document-list.types"
import { getFilterTypeChipLabel, getSortMenuLabel } from "./document-list.labels"
import {
  displayFolderTitleForStoragePath,
  displaySegmentsForStoragePath,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"
import { useWorkspace } from "@/providers/workspace-provider"
import { fetchWithAuth } from "@/lib/api"
import { openBasecampTodoInFlowNotes } from "@/lib/basecamp-todo-integration"
import { basecampViewTodoLabel } from "@/lib/basecamp-todo-open-labels"
import { OpenDestinationIcons, resolveOpenExternalIcon } from "@/lib/open-destination-icons"
import { BasecampIcon } from "@/components/icons/basecamp-icon"
import { deriveProjectSlug } from "@/lib/launch-cursor-uri"
import { useContainerWidth } from "@/hooks/use-container-width"
import { CursorWorkspaceMenu } from "./cursor-workspace-menu"
import { toast } from "sonner"

/** Below this measured toolbar width, secondary todo-strip actions collapse into the "More options" menu. */
const TODO_STRIP_NARROW_BREAKPOINT = 560
const OpenInBasecampIcon = resolveOpenExternalIcon(BasecampIcon)

export interface DocumentListToolbarProps {
  projectFilter?: string | null
  onFolderClick?: (path: string) => void
  onGoBack?: () => void
  onGoForward?: () => void
  canGoBack: boolean
  canGoForward: boolean
  onNewDocument?: () => void
  /** Current folder browse path (projectFilter when not a UUID). Used for create dropdown. */
  currentFolderPath?: string | null
  onTogglePreviewPane?: () => void
  previewPaneOpen: boolean
  onReload?: () => void
  isReloading?: boolean
  workspaceColor?: string
  todoIdFromPath: string | null
  todoStripData: TodoFolderStripData | null
  workSpecDocumentId: string | null
  onOpenWorkSpec?: () => void
  /** Unfiltered document types for this folder (chips stay visible while filtered). */
  folderTypeInventory: string[]
  typeFilter: FilterType
  setTypeFilter: (f: FilterType) => void
  authorFilter: string
  setAuthorFilter: (a: string) => void
  statusFilter: "active" | "all"
  setStatusFilter: (s: "active" | "all") => void
  showAuthorMenu: boolean
  setShowAuthorMenu: (v: boolean) => void
  showSortMenu: boolean
  setShowSortMenu: (v: boolean) => void
  authors: string[]
  sortBy: ListSortColumn
  sortDir: SortDir
  setSortBy: (s: ListSortColumn) => void
  setSortDir: (d: SortDir) => void
  viewMode: ViewMode
  setViewMode: (m: ViewMode) => void
  documentsLength: number
  totalDocuments: number
  workspaces?: WorkspaceRouteInfo[]
}

/** Compact card/grid sort presets (list view uses column headers instead). */
const GRID_SORT_PRESETS: { column: ListSortColumn; dir: SortDir }[] = [
  { column: "modified", dir: "desc" },
  { column: "modified", dir: "asc" },
  { column: "name", dir: "asc" },
  { column: "name", dir: "desc" },
]

export function DocumentListToolbar({
  projectFilter,
  onFolderClick,
  onGoBack,
  onGoForward,
  canGoBack,
  canGoForward,
  onNewDocument,
  currentFolderPath,
  onTogglePreviewPane,
  previewPaneOpen,
  onReload,
  isReloading = false,
  workspaceColor,
  todoIdFromPath,
  todoStripData,
  workSpecDocumentId,
  onOpenWorkSpec,
  workspaces: workspacesProp = [],
}: DocumentListToolbarProps) {
  const { workspaces: contextWorkspaces } = useWorkspace()
  const workspacesRoute = useMemo((): WorkspaceRouteInfo[] => {
    if (workspacesProp.length > 0) return workspacesProp
    return contextWorkspaces.map((w) => ({
      id: w.id,
      slug: w.slug,
      name: w.name,
      contentRoot: w.contentRoot,
    }))
  }, [workspacesProp, contextWorkspaces])
  const [openingTodoTab, setOpeningTodoTab] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  const toolbarRef = useRef<HTMLElement>(null)
  const containerWidth = useContainerWidth(toolbarRef)
  const isNarrowToolbar = containerWidth !== null && containerWidth < TODO_STRIP_NARROW_BREAKPOINT

  const cursorProjectName = todoStripData?.projectName ?? null
  const cursorProjectSlug = deriveProjectSlug(cursorProjectName)
  const cursorBasecampProjectId = todoStripData?.projectId ?? null

  const handleViewTodoInFlowNotes = async () => {
    if (!todoIdFromPath) return
    let pid = todoStripData?.projectId ?? null
    let recordType: 'todo' | 'todolist' = todoStripData?.recordType ?? 'todo'
    if (!pid) {
      setOpeningTodoTab(true)
      try {
        const res = await fetchWithAuth(`/api/basecamp/search-todo/${todoIdFromPath}`)
        const data = res.ok ? await res.json() : null
        if (data?.found && data?.todo?.projectId) {
          pid = String(data.todo.projectId)
          if (data.todo.recordType === 'todolist' || data.todo.recordType === 'todo') {
            recordType = data.todo.recordType
          }
        }
      } finally {
        setOpeningTodoTab(false)
      }
    }
    if (pid) {
      openBasecampTodoInFlowNotes(todoIdFromPath, pid, recordType)
    } else {
      toast.error("Could not open todo", {
        description: "Basecamp project ID was not found for this todo.",
      })
    }
  }

  const isUuidProjectFilter =
    !!projectFilter &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(projectFilter)
  const normalizedFolderPath = (currentFolderPath ?? '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')

  const handleCreateDocument = () => {
    if (normalizedFolderPath) {
      window.dispatchEvent(
        new CustomEvent('create-file-at-path', { detail: { folderPath: normalizedFolderPath } }),
      )
    } else {
      onNewDocument?.()
    }
  }

  const handleCreateFolder = () => {
    window.dispatchEvent(
      new CustomEvent('create-folder-at-path', { detail: { parentPath: normalizedFolderPath } }),
    )
  }

  // Rename / move / delete are handled by the left sidebar (same dialogs as its folder context menu).
  const isWorkspaceRootFolder = workspacesRoute.some(
    (w) => (w.contentRoot ?? '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '') === normalizedFolderPath,
  )
  const canManageFolder = !!normalizedFolderPath && !isUuidProjectFilter && !isWorkspaceRootFolder
  const dispatchFolderAction = (eventName: 'rename-folder-at-path' | 'move-folder-at-path' | 'delete-folder-at-path') => {
    window.dispatchEvent(new CustomEvent(eventName, { detail: { path: normalizedFolderPath } }))
    setShowMoreMenu(false)
  }

  const showPathBreadcrumbs = !!projectFilter && !isUuidProjectFilter

  const folderTitle = projectFilter
    ? isUuidProjectFilter
      ? "Project Documents"
      : workspacesRoute.length > 0
        ? displayFolderTitleForStoragePath(projectFilter, workspacesRoute)
        : projectFilter.includes("/")
          ? projectFilter.split("/").pop() ?? projectFilter
          : projectFilter
    : "Folders"

  const folderIconStyle = workspaceColor ? { color: workspaceColor } : undefined

  return (
    <>
      <nav
        ref={toolbarRef}
        className="flex items-center justify-between px-4 py-2 border-b border-border bg-background/50 document-list-toolbar"
        aria-label="Folder actions"
        data-testid={testIds.documentList.toolbar}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <NavigationButtons
            onGoBack={onGoBack}
            onGoForward={onGoForward}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            backTestId="explorer-back-button"
            forwardTestId="explorer-forward-button"
          />

          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {showPathBreadcrumbs &&
              (() => {
                const displaySegments = displaySegmentsForStoragePath(
                  projectFilter ?? "",
                  workspacesRoute,
                )
                const parentSegments = displaySegments.slice(0, -1)
                if (parentSegments.length === 0) return null

                const breadcrumbSegments: BreadcrumbSegment[] = parentSegments.map((segment) => ({
                  label: segment.label,
                  path: segment.storagePath,
                  onClick: () => onFolderClick?.(segment.storagePath),
                }))
                return <AddressBar segments={breadcrumbSegments} containerWidth={containerWidth} />
              })()}

            <h1
              className="text-lg font-semibold text-foreground flex items-center gap-2 min-w-0"
              aria-label="Folder title"
              data-testid={testIds.documentList.title}
            >
              {!isUuidProjectFilter && (
                <Folder
                  className="h-4 w-4 shrink-0 text-blue-500"
                  style={folderIconStyle}
                  aria-hidden
                />
              )}
              <span className="truncate">{folderTitle}</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {onReload && (
            <IconButton
              icon={
                <RefreshCw
                  className={`h-4 w-4 ${isReloading ? "animate-spin" : ""}`}
                />
              }
              label={isReloading ? "Reloading folder..." : "Reload folder"}
              variant="ghost"
              size="sm"
              onClick={onReload}
              disabled={isReloading}
              loading={isReloading}
              data-testid={testIds.documentList.reloadButton}
            />
          )}

          {onTogglePreviewPane ? (
            <PreviewPanelToggle
              open={previewPaneOpen}
              onToggle={onTogglePreviewPane}
              testId={testIds.documentList.previewToggle}
            />
          ) : null}

          <div className="relative">
            <button
              type="button"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent hover:text-accent-foreground"
              aria-label="More options"
              title="More options"
              aria-expanded={showMoreMenu}
              data-testid={testIds.documentList.menuButton}
              onClick={() => setShowMoreMenu((open) => !open)}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {showMoreMenu && (
              <div className="absolute top-full mt-1 right-0 z-50 min-w-[180px] rounded-md border border-border bg-background p-1 shadow-lg">
                {onTogglePreviewPane && (
                  <button
                    type="button"
                    className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                    onClick={() => {
                      onTogglePreviewPane()
                      setShowMoreMenu(false)
                    }}
                  >
                    <PanelRight className="mr-2 h-4 w-4" />
                    {previewPaneOpen ? "Hide preview pane" : "Show preview pane"}
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("flownotes-focus-view"))
                    setShowMoreMenu(false)
                  }}
                >
                  <Maximize2 className="mr-2 h-4 w-4" />
                  Focus view
                </button>
                <div className="my-1 h-px bg-border" />
                <button
                  type="button"
                  className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                  data-testid={testIds.documentList.newDocument}
                  onClick={() => {
                    handleCreateDocument()
                    setShowMoreMenu(false)
                  }}
                >
                  <FilePlus className="mr-2 h-4 w-4" />
                  New document
                </button>
                <button
                  type="button"
                  className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                  data-testid={testIds.documentList.newFolder}
                  onClick={() => {
                    handleCreateFolder()
                    setShowMoreMenu(false)
                  }}
                >
                  <FolderPlus className="mr-2 h-4 w-4" />
                  New folder
                </button>
                {canManageFolder && (
                  <>
                    <div className="my-1 h-px bg-border" />
                    <button
                      type="button"
                      className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                      data-testid={testIds.documentList.renameFolder}
                      onClick={() => dispatchFolderAction('rename-folder-at-path')}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Rename
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                      data-testid={testIds.documentList.moveFolder}
                      onClick={() => dispatchFolderAction('move-folder-at-path')}
                    >
                      <Move className="mr-2 h-4 w-4" />
                      Move to…
                    </button>
                    <div className="my-1 h-px bg-border" />
                    <button
                      type="button"
                      className="flex w-full items-center rounded px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                      data-testid={testIds.documentList.deleteFolder}
                      onClick={() => dispatchFolderAction('delete-folder-at-path')}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete folder
                    </button>
                  </>
                )}
                {todoIdFromPath && todoStripData && (
                  <>
                    <div className="my-1 h-px bg-border" />
                    {workSpecDocumentId && onOpenWorkSpec && (
                      <button
                        type="button"
                        className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                        data-testid={testIds.documentList.todoStripWorkSpec}
                        onClick={() => {
                          onOpenWorkSpec()
                          setShowMoreMenu(false)
                        }}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Work spec
                      </button>
                    )}
                    {todoStripData.appUrl && (
                      <a
                        href={todoStripData.appUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-muted"
                        data-testid={testIds.documentList.todoStripOpenBasecamp}
                        onClick={() => setShowMoreMenu(false)}
                      >
                        <OpenInBasecampIcon className="mr-2 h-4 w-4" />
                        Open in Basecamp
                      </a>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {todoIdFromPath && todoStripData && (
        <div className="flex flex-col gap-1.5 px-4 py-2 text-sm">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <Badge
              variant="outline"
              className={`text-xs shrink-0 ${
                todoStripData.completed
                  ? "border-green-500/40 text-green-600 dark:text-green-400"
                  : "border-border text-muted-foreground"
              }`}
            >
              {todoStripData.statusLabel}
            </Badge>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={openingTodoTab}
              onClick={() => void handleViewTodoInFlowNotes()}
              className="h-8 gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
              title={basecampViewTodoLabel(todoStripData.recordType)}
              data-testid={testIds.documentList.todoStripViewInFlownotes}
            >
              {openingTodoTab ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <OpenDestinationIcons.open className="h-3 w-3" />
              )}
              {basecampViewTodoLabel(todoStripData.recordType)}
            </Button>
            {/* Open in Basecamp / Work spec moved into the "More options" (⋮) menu above -
                persistent-vs-menu split applies regardless of toolbar width (Phase 2). */}
            <CursorWorkspaceMenu
              todoId={todoIdFromPath}
              todoTitle={todoStripData.title}
              documentId={workSpecDocumentId}
              projectName={cursorProjectName}
              projectSlug={cursorProjectSlug}
              basecampProjectId={cursorBasecampProjectId}
              folderPath={normalizedFolderPath || null}
              compact={isNarrowToolbar}
              data-testid={testIds.documentList.todoStripOpenInCursor}
            />
          </div>
          {todoStripData.lastCommentPreview && (
            <div className="flex items-start gap-1.5 text-xs text-muted-foreground min-w-0">
              <MessageSquare className="h-3.5 w-3.5 shrink-0 mt-0.5 opacity-80" aria-hidden />
              <span className="line-clamp-2" title={todoStripData.lastCommentPreview}>
                Last comment: {todoStripData.lastCommentPreview}
              </span>
            </div>
          )}
        </div>
      )}

    </>
  )
}

export function DocumentListFilterRow({
  folderTypeInventory,
  typeFilter,
  setTypeFilter,
  authorFilter,
  setAuthorFilter,
  statusFilter,
  setStatusFilter,
  showAuthorMenu,
  setShowAuthorMenu,
  showSortMenu,
  setShowSortMenu,
  authors,
  sortBy,
  sortDir,
  setSortBy,
  setSortDir,
  viewMode,
  setViewMode,
  documentsLength,
  totalDocuments,
}: Pick<
  DocumentListToolbarProps,
  | 'folderTypeInventory'
  | 'typeFilter'
  | 'setTypeFilter'
  | 'authorFilter'
  | 'setAuthorFilter'
  | 'statusFilter'
  | 'setStatusFilter'
  | 'showAuthorMenu'
  | 'setShowAuthorMenu'
  | 'showSortMenu'
  | 'setShowSortMenu'
  | 'authors'
  | 'sortBy'
  | 'sortDir'
  | 'setSortBy'
  | 'setSortDir'
  | 'viewMode'
  | 'setViewMode'
  | 'documentsLength'
  | 'totalDocuments'
>) {
  return (
      <div
        className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-background px-4"
        data-testid={testIds.documentList.filters}
        aria-label="Document filters"
      >
        {(() => {
          const typeSet = new Set(
            folderTypeInventory.filter((t) => typeof t === "string" && t.length > 0 && t !== "all"),
          )
          if (typeFilter !== "all") typeSet.add(String(typeFilter))
          const availableTypes = [...typeSet] as FilterType[]
          // Keep chips while filtered even if the filtered list only has one type
          const showChips = availableTypes.length > 1 || typeFilter !== "all"
          return showChips ? (
            <div
              className="flex items-center gap-2 flex-wrap"
              data-testid={testIds.documentList.typeFilter}
              role="group"
              aria-label="Document type filter"
            >
              <button
                type="button"
                onClick={() => setTypeFilter("all")}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  typeFilter === "all"
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                }`}
              >
                All
              </button>
              {availableTypes.map((type) => (
                <button
                  type="button"
                  key={type}
                  onClick={() => setTypeFilter(typeFilter === type ? "all" : type)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    typeFilter === type
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {getFilterTypeChipLabel(type)}
                </button>
              ))}
            </div>
          ) : null
        })()}

        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className={`gap-1 ${
              authorFilter !== "all" || statusFilter !== "active" || typeFilter !== "all"
                ? "border-primary/50 text-primary"
                : ""
            }`}
            onClick={() => setShowAuthorMenu(!showAuthorMenu)}
            aria-label="More filters"
          >
            <SlidersHorizontal className="h-3 w-3" />
            Filters
            {(authorFilter !== "all" || statusFilter !== "active" || typeFilter !== "all") && (
              <span className="ml-0.5 px-1 py-0 rounded-full bg-primary text-primary-foreground text-[10px] leading-4">
                {[authorFilter !== "all", statusFilter !== "active", typeFilter !== "all"].filter(Boolean).length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          </Button>
          {showAuthorMenu && (
            <div className="absolute top-full mt-1 left-0 bg-background border border-border rounded-md shadow-lg z-50 min-w-[180px] p-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1 mb-1">
                Author
              </div>
              {authors.map((author) => (
                <button
                  key={author}
                  onClick={() => setAuthorFilter(author)}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors flex items-center gap-2 ${
                    authorFilter === author ? "bg-muted font-medium" : ""
                  }`}
                >
                  {authorFilter === author && <span className="text-primary text-xs">✓</span>}
                  <span className={authorFilter === author ? "ml-0" : "ml-3.5"}>
                    {author === "all" ? "All Authors" : author}
                  </span>
                </button>
              ))}
              <div className="border-t border-border my-2" />
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1 mb-1">
                Status
              </div>
              {(["active", "all"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors flex items-center gap-2 ${
                    statusFilter === s ? "bg-muted font-medium" : ""
                  }`}
                >
                  {statusFilter === s && <span className="text-primary text-xs">✓</span>}
                  <span className={statusFilter === s ? "ml-0" : "ml-3.5"}>
                    {s === "active" ? "Active Only" : "Include Archived"}
                  </span>
                </button>
              ))}
              {(authorFilter !== "all" || statusFilter !== "active" || typeFilter !== "all") && (
                <>
                  <div className="border-t border-border my-2" />
                  <button
                    onClick={() => {
                      setTypeFilter("all")
                      setAuthorFilter("all")
                      setStatusFilter("active")
                      setShowAuthorMenu(false)
                    }}
                    className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors"
                  >
                    Reset filters
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* List view: sort via column headers. Card/grid keep a compact control. */}
        {viewMode !== "list" && (
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="gap-1 border-border/60"
              onClick={() => setShowSortMenu(!showSortMenu)}
              aria-label={`Sort: ${getSortMenuLabel(sortBy, sortDir)}`}
              aria-expanded={showSortMenu}
              data-testid={testIds.documentList.sort}
            >
              {getSortMenuLabel(sortBy, sortDir)}
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </Button>
            {showSortMenu && (
              <div className="absolute top-full mt-1 left-0 z-50 min-w-[160px] rounded-md border border-border/60 bg-background p-1 shadow-sm">
                {GRID_SORT_PRESETS.map((preset) => {
                  const active = sortBy === preset.column && sortDir === preset.dir
                  const key = `${preset.column}-${preset.dir}`
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setSortBy(preset.column)
                        setSortDir(preset.dir)
                        setShowSortMenu(false)
                      }}
                      className={`w-full rounded-sm px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                        active ? "bg-muted font-medium" : ""
                      }`}
                    >
                      {getSortMenuLabel(preset.column, preset.dir)}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {typeFilter !== "all" && (
          <div className="text-xs text-muted-foreground">
            {documentsLength === totalDocuments
              ? `${documentsLength} doc${documentsLength === 1 ? "" : "s"}`
              : `${documentsLength} of ${totalDocuments} docs`}
          </div>
        )}

        <ListViewModeToggle
          value={viewMode === "tree" ? "list" : viewMode}
          onChange={(mode) => setViewMode(mode)}
          testId={testIds.documentList.viewMode}
        />
      </div>
  )
}
